import React, { useState, useRef } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { useTheme } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import GlassmorphismDesignSystem from '../theme/GlassmorphismDesignSystem';

const DatePickerUTC = ({
  value,
  onChange,
  onBlur,
  timePicker = false,
  label,
  fullWidth = true,
  error,
  help,
  required,
  highlightedDates = [],
  clearable = false,
  disabled,
  fieldKey,
  locale,
  localeText,
  format,
  sx: outerSx,
  ...props
}) => {
  const theme = useTheme();

  // Valeur brouillon pendant l'édition : le picker est contrôlé, donc sans
  // état local les sélections intermédiaires (jour, heure) seraient perdues
  // puisqu'on ne committe au parent que sur onAccept.
  // `undefined` = pas de brouillon ; `null` = brouillon vidé (clear/Backspace).
  const [draftValue, setDraftValue] = useState(undefined);
  const isPopupOpenRef = useRef(false);

  const dateOnChange = (newValue, date) => {
    const fieldType = timePicker ? 'date-time' : 'date';

    if (newValue === null) {
      onChange(null, fieldType, null);
      if (onBlur) {
        onBlur(null, fieldType, null);
      }
      return;
    }

    const isValid = moment(date).isValid();
    if (isValid) {
      onChange(newValue, fieldType, date);
      if (onBlur) {
        onBlur(newValue, fieldType, date);
      }
    }
  };

  // Utiliser onAccept plutôt que onChange : onChange se déclenche aussi sur les
  // sélections intermédiaires (ex. choisir une année) et fermerait l'éditeur
  // avant que l'utilisateur puisse changer de mois ou choisir le jour final.
  const handleAccept = (newValue) => {
    setDraftValue(undefined);
    dateOnChange(newValue, newValue);
  };

  const handleClose = () => {
    isPopupOpenRef.current = false;
    setDraftValue(undefined);
  };

  // La saisie clavier (y compris l'effacement) ne passe jamais par onAccept :
  // on committe le brouillon quand le champ perd le focus, seulement si le
  // calendrier est fermé (sinon un clic dans le calendrier committerait une
  // valeur intermédiaire).
  const handleFieldBlur = () => {
    if (!isPopupOpenRef.current && draftValue !== undefined) {
      setDraftValue(undefined);
      dateOnChange(draftValue, draftValue);
    }
  };

  const PickerComponent = timePicker ? DateTimePicker : DatePicker;
  let momentValue = null;

  if (value?._seconds) {
    momentValue = value?._seconds ? moment.unix(value?._seconds) : null;
  } else if (value) {
    momentValue = moment(value);
  }

  if (momentValue && !momentValue.isValid()) {
    momentValue = null;
  }

  const renderDay = (props) => {
    const { day, outsideCurrentMonth, ...other } = props;

    if (!day || !moment(day).isValid()) {
      return (
        <PickersDay
          {...other}
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
        />
      );
    }

    const dateStr = moment(day).format('YYYY-MM-DD');
    const isHighlighted = highlightedDates?.includes(dateStr);

    if (isHighlighted && !outsideCurrentMonth) {
      return (
        <Box sx={{ position: 'relative' }}>
          <PickersDay
            {...other}
            day={day}
            outsideCurrentMonth={outsideCurrentMonth}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: theme.palette.primary.main,
              zIndex: 1,
            }}
          />
        </Box>
      );
    }

    return (
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
      />
    );
  };

  const isDarkmode = theme.palette.mode === 'dark';

  return (
    <Box sx={{ width: '100%', ...outerSx }}>
      {label && (
        <Typography
          component="label"
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            color: 'text.secondary',
            mb: '2px',
            display: 'block',
          }}
        >
          {label}
          {required && ' *'}
        </Typography>
      )}
      <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale} localeText={localeText}>
        <PickerComponent
          format={format}
          value={draftValue !== undefined ? draftValue : momentValue}
          key={fieldKey || 'date'}
          onChange={(newValue) => setDraftValue(newValue)}
          onAccept={handleAccept}
          onOpen={() => {
            isPopupOpenRef.current = true;
          }}
          onClose={handleClose}
          // DateTimePicker a closeOnSelect=false par défaut (contrairement à
          // DatePicker) : sans ceci, onAccept n'arriverait qu'au clic en dehors
          // du calendrier, jamais juste en choisissant les minutes.
          closeOnSelect
          disabled={disabled}
          slots={{
            day: renderDay,
          }}
          slotProps={{
            popper: {
              sx: {
                zIndex: 99999,
              },
            },
            field: {
              clearable,
            },
            textField: {
              fullWidth,
              ...(error !== undefined ? {error: Boolean(error)} : {}),
              helperText: help,
              size: 'small',
              onBlur: handleFieldBlur,
              sx: {
                '& .MuiInputBase-root': {
                  borderRadius: '8px !important',
                  height: '36px',
                  background:
                    GlassmorphismDesignSystem.patterns.interactive.background(theme),
                  backdropFilter:
                    GlassmorphismDesignSystem.patterns.interactive.backdropFilter,
                },
                '& .MuiInputBase-input': {
                  padding: '6px 12px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '8px !important',
                  borderColor: isDarkmode
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.12)',
                },
                '& .MuiInputAdornment-root': {
                  marginRight: '4px',
                  '& .MuiSvgIcon-root': {
                    fontSize: '1rem',
                  },
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default DatePickerUTC;
